import type { ClientMetadata, TopicName } from 'magpie-types';
import { MAGPIE_CLIENT_LOGGER_NAME, MetricsReporter } from '../config';
import { ConnectionError } from '../errors';
import { Level, Logger } from 'simple-logging-lib';
import { MagpieDebugHandler, MagpieErrorHandler } from '../logging';
import {
  MagpieConfigManager,
  Topic,
  TopicDefinition,
  fromConnectionSpec,
} from '../models';
import { getDeferred, safeCallback, type Deferred } from '../utils';
import type { IConnectionStrategy } from './abstract-connection-strategy';
import { type ConnectionSpec } from './connection-spec';
import { ConnectionStateManager, ConnectionStatus } from './connection-status';
import type { MessagingProxy } from './messaging-proxy';
import { Unsubscribe } from '../types';
import { WindowBrokerStrategy } from './window-broker-strategy';

export interface ForTopicOptions {
  subscribe: boolean;
}

const defaultForTopicOptions: ForTopicOptions = {
  subscribe: true,
};

export class MagpieConnection {
  protected static connection: MagpieConnection | undefined;
  protected readonly logger = Logger.getLogger(MAGPIE_CLIENT_LOGGER_NAME);
  private connectionStrategy: IConnectionStrategy | null = null;
  protected proxy: Deferred<MessagingProxy> = getDeferred();
  protected readonly clientMetadata: ClientMetadata;
  protected readonly configManager: MagpieConfigManager;

  /**
   * `#topics` is a WeakMap that stores a mapping of `TopicDefinition` to
   * `Topic` instances. This is used to cache `Topic` instances and reuse them
   * for subsequent calls to `forTopic`.
   * This is useful for scenarios where multiple components need to interact
   * with the same topic. `WeakMap` is used to avoid memory leaks when the
   * `TopicDefinition` is garbage collected.
   */
  #topics: WeakMap<TopicDefinition<any>, Topic<any>> = new WeakMap();

  protected constructor(private readonly spec: Partial<ConnectionSpec> = {}) {
    // Get the config manager and provision it with the connection spec
    const overrides = fromConnectionSpec(spec);
    this.configManager = MagpieConfigManager.getInstance(overrides);

    // Lock the config manager to prevent further changes
    this.configManager.lock();

    this.clientMetadata = this.configManager.getClientMetadata();

    this.setupLogger();
  }

  /**
   * `MagpieConnection.create` is a factory method that returns a singleton
   * instance of `MagpieConnection`. It initializes the connection if it
   * doesn't exist. It's not required anymore, but it's kept for backwards
   * compatibility.
   */
  static create = (spec: Partial<ConnectionSpec> = {}): MagpieConnection => {
    if (!MagpieConnection.connection) {
      const connection = new MagpieConnection(spec);
      connection.connect();
      MagpieConnection.connection = connection;
    }

    return MagpieConnection.connection;
  };

  /**
   * `MagpieConnection.get` is a "get or create" method that returns a singleton
   * instance of `MagpieConnection`. It initializes the connection if it
   * doesn't exist.
   */
  static get = (): MagpieConnection => {
    if (!MagpieConnection.connection) {
      const connection = new MagpieConnection();
      connection.connect();
      MagpieConnection.connection = connection;
    }

    return MagpieConnection.connection;
  };

  private setupLogger = () => {
    const debug = this.configManager.get('debug');
    let debugAll = false;
    try {
      debugAll = localStorage.getItem('MAGPIE_DEBUG_ALL') === 'true';
    } catch (error) {
      /* NO-OP */
    }
    const loggingLevel = debug || debugAll ? Level.DEBUG : Level.ERROR;
    this.logger.setLevel(loggingLevel);
    this.logger.addHandler(new MagpieDebugHandler(this.clientMetadata));
    this.logger.addHandler(new MagpieErrorHandler(this.clientMetadata));
  };

  protected connect = (): void => {
    const onError = this.configManager.get('onConnectionError');
    const connectionState = ConnectionStateManager.getInstance();
    if (!connectionState.canConnect()) {
      return;
    }
    connectionState.setStatus(ConnectionStatus.CONNECTING);

    const strategy = this.configManager.get('strategy');
    const debug = this.configManager.get('debug');
    const safeOnError = onError && safeCallback(onError);

    const connectionStrategy =
      strategy ||
      new WindowBrokerStrategy({
        clientMetadata: this.clientMetadata,
        debug,
      });

    this.connectionStrategy = connectionStrategy;
    this.connectionStrategy
      .open()
      .then((proxy) => {
        this.proxy.resolve(proxy);
        this.logger.debug('Connection opened');
        connectionState.setStatus(ConnectionStatus.CONNECTED);
        window.addEventListener('pagehide', this.onPageHide);
      })
      .catch((cause) => {
        const status = ConnectionStatus.FAILED_TO_CONNECT;
        connectionState.setStatus(status);
        const error = new ConnectionError(
          ConnectionError.Reason.FAILED_TO_OPEN,
          { originalError: cause, messageArgs: [status] }
        );
        this.logger.error(error);
        MetricsReporter.reportConnectionError({
          ...this.clientMetadata,
          status,
        });
        if (safeOnError) {
          safeOnError(error);
        }
      });
  };

  private onPageHide = (event: PageTransitionEvent) => {
    if (!event.persisted) {
      // The page is being unloaded, so we should disconnect
      this.disconnect();
    }
  };

  private runDisconnect = async (): Promise<void> => {
    const connectionState = ConnectionStateManager.getInstance();
    if (!connectionState.canDisconnect()) {
      return Promise.resolve();
    }
    connectionState.setStatus(ConnectionStatus.DISCONNECTING);
    if (this.connectionStrategy) {
      await this.connectionStrategy.close();
      this.logger.debug('Connection closed');
      MagpieConnection.connection = undefined;
      window.removeEventListener('pagehide', this.onPageHide);
      connectionState.setStatus(ConnectionStatus.DISCONNECTED);
    }
  };

  public disconnect = (): void => {
    this.logger.debug('Disconnecting');
    this.runDisconnect().catch((error) => {
      this.logger.error(`Failed to close connection.\nCause: ${error.message}`);
    });
  };

  /**
   * `forTopic` returns a `Topic` instance for a given `TopicDefinition`.
   * It subscribes to the topic if `subscribe` is set to `true` in the options
   * (default behavior).
   * `Topic` instances are cached and reused for subsequent calls to `forTopic`.
   * `Topic` is a facade for interacting with a topic, including subscribing,
   * unsubscribing, and publishing messages.
   * The method will wait for the connection to be established before returning
   * the `Topic` instance. If the connection is not established within 5 seconds,
   * it will throw an error.
   */
  public forTopic = <TopicDef extends TopicDefinition<any>>(
    topicDefinition: TopicDef,
    options: Partial<ForTopicOptions> = defaultForTopicOptions
  ): Topic<TopicDef> => {
    const { subscribe: shouldSubscribe } = {
      ...defaultForTopicOptions,
      ...options,
    };

    if (this.#topics.has(topicDefinition)) {
      return this.#topics.get(topicDefinition) as Topic<TopicDef>;
    }

    const topic = new Topic(
      topicDefinition,
      this.proxy.promise,
      this.clientMetadata
    );
    this.#topics.set(topicDefinition, topic);
    if (shouldSubscribe) {
      topic
        .subscribe()
        .then(() => {
          this.logger.debug(`Subscribed to topic ${topicDefinition.getName()}`);
        })
        .catch((e) => {
          this.logger.error('Failed to subscribe to topic', e);
          setTimeout(() => {
            throw e;
          }, 0);
        });
    }
    return topic;
  };

  /**
   * @deprecated Use `forTopic` instead. The `useTopic` method name conflicts
   * with React Compiler's heuristic for identifying hooks (identifiers starting
   * with "use" are treated as hooks). This causes false positive errors in React
   * components even though `useTopic` is not a React hook.
   *
   * `useTopic` returns a `Topic` instance for a given `TopicDefinition`.
   * It subscribes to the topic if `subscribe` is set to `true` in the options
   * (default behavior).
   * `Topic` instances are cached and reused for subsequent calls.
   * `Topic` is a facade for interacting with a topic, including subscribing,
   * unsubscribing, and publishing messages.
   * The method will wait for the connection to be established before returning
   * the `Topic` instance. If the connection is not established within 5 seconds,
   * it will throw an error.
   */
  public useTopic = <TopicDef extends TopicDefinition<any>>(
    topicDefinition: TopicDef,
    options: Partial<ForTopicOptions> = defaultForTopicOptions
  ): Topic<TopicDef> => {
    return this.forTopic(topicDefinition, options);
  };

  public getClientId = () =>
    this.connectionStrategy?.getHandshakeResult()?.clientId;

  public getActiveTopicSubscriptions = () =>
    this.connectionStrategy?.getActiveTopicSubscriptions() ??
    Promise.resolve<ReadonlyArray<TopicName>>([]);

  public onTopicSubscriptionsChange = (
    onChange: (topics: ReadonlyArray<TopicName>) => void
  ): Unsubscribe => {
    if (!this.connectionStrategy) {
      const connectionState = ConnectionStateManager.getInstance();
      const status = connectionState.getStatus();
      throw new ConnectionError(ConnectionError.Reason.UNINITIALIZED, {
        messageArgs: [status],
      });
    }
    return this.connectionStrategy.onTopicSubscriptionsChange(onChange);
  };

  /**
   * Only for testing purposes
   */
  static resetInstance = () => {
    ConnectionStateManager.getInstance().setStatus(
      ConnectionStatus.UNINITIALIZED
    );
    MagpieConnection.connection?.disconnect();
    MagpieConnection.connection = undefined;
    MagpieConfigManager.resetInstance();
  };

  public setDebugMode = (debugMode: boolean) => {
    this.configManager.setDebugMode(debugMode);
    const loggingLevel = debugMode ? Level.DEBUG : Level.ERROR;
    this.logger.setLevel(loggingLevel);
  };
}
